import { PDFGenerator } from "./pdf-generator";
import { BasePdfGenerator } from "./base-generator";
import { WholesaleRequest } from "./wholesale-request.interface";

export class WholesaleRequestListGenerator extends BasePdfGenerator {
  private data: WholesaleRequest;

  constructor(wholesaleRequest: WholesaleRequest) {
    super();
    this.data = wholesaleRequest;
    const docHeight = this.calculateDocumentHeight();

    this.pdf = new PDFGenerator({ height: docHeight < 100 ? 100 : docHeight });
    this.doc = this.pdf.getDoc();

    this.pdf.setup();

    // super(docHeight);

    this.generate();
  }

  private generate() {
    this.setTitle();

    this.addCustomerName();
    // this.addBrandName();
    this.addItems();
  }

  private calculateDocumentHeight() {
    //   consider title, supplier name, brand name, items table
    let height = 0;

    // Title
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER * 2,
      this.titleFontSize,
    );

    // Supplier Name
    if (this.data.customerName) {
      height += this.getRequiredHeight(
        this.TEXT_HEIGHT_MULTIPLIER,
        this.fontSize,
      );
    }

    // Brand Name
    if (this.data.date) {
      height += this.getRequiredHeight(
        this.TEXT_HEIGHT_MULTIPLIER,
        this.fontSize,
      );
    }

    // Items Table
    const itemsCount = this.data.items.length;
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER,
      this.fontSize,
    ); // Table Header
    height +=
      itemsCount *
      this.getRequiredHeight(this.TEXT_HEIGHT_MULTIPLIER, this.fontSize); // Table Rows

    // Add some extra space
    height += this.getRequiredHeight(
      this.TEXT_HEIGHT_MULTIPLIER,
      this.fontSize,
    );

    return Math.ceil(height);
  }

  private addItems() {
    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER;

    const reducedByBrandData = this.data.items.reduce(
      (acc, item) => {
        if (!acc[item.brandName]) {
          acc[item.brandName] = [];
        }
        acc[item.brandName].push(item);
        acc[item.brandName].sort((a, b) =>
          a.productName.localeCompare(b.productName),
        );
        return acc;
      },
      {} as Record<string, typeof this.data.items>,
    );

    // I want this list like first row will be brand name
    // and below that all items of that brand
    const itemsSorted: typeof this.data.items = [];
    for (const brand in reducedByBrandData) {
      // Push a dummy item as brand name row
      // itemsSorted.push({
      //   productName: brand,
      //   retailPrice: "",
      //   stockQuantity: "",
      //   quantity: "",
      //   brandName: brand,
      // } as any);

      // Push all items of that brand
      itemsSorted.push(...reducedByBrandData[brand]);
    }

    // this.data.items = itemsSorted;

    const tableData = itemsSorted.map((item, _i) => [
      typeof item.quantity === "number"
        ? item.quantity.toString()
        : item.quantity,
      item.productName,

      typeof item.retailPrice === "number"
        ? item.retailPrice.toFixed(2)
        : item.retailPrice,
      typeof item.stockQuantity === "number"
        ? item.stockQuantity.toString()
        : item.stockQuantity,
    ]);

    (this.doc as any).autoTable({
      startY: this.calculateRequiredHeight(heightPoint),
      head: [["Quantity", `Items (${this.data.items.length})`, "MRP", "Stock"]],
      body: tableData,
      theme: "plain",

      styles: {
        font: this.fontFamily,
        fontSize: this.fontSize - this.fontSize * 0.2,

        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        overflow: "linebreak",
        cellPadding: 1,
        fontStyle: "normal",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
        fontSize: this.fontSize - this.fontSize * 0.2,
        fontStyle: "bold",
        halign: "right",
        valign: "middle",
        // We'll custom draw borders so keep lineWidth 0 here
        lineWidth: 0,
      },
      margin: { left: this.margin },
      tableWidth: this.pageWidth - 2 * this.margin,

      columnStyles: {
        0: {
          cellWidth: this.pdf.calculateTableWidth(18),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: this.fontSize * 0.1,
            right: this.fontSize * 0.1,
          },
          overflow: "linebreak",
          halign: "center",
        }, // Item
        1: {
          cellWidth: this.pdf.calculateTableWidth(40),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: this.fontSize * 0.1,
            right: this.fontSize * 0.1,
          },
          halign: "left",
        }, // Price
        2: {
          cellWidth: this.pdf.calculateTableWidth(18),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: this.fontSize * 0.1,
            right: this.fontSize * 0.1,
          },
          halign: "right",
        }, // Qty
        3: {
          cellWidth: this.pdf.calculateTableWidth(18),
          cellPadding: {
            top: this.fontSize * 0.1,
            bottom: this.fontSize * 0.1,
            left: this.fontSize * 0.1,
            right: this.fontSize * 0.1,
          },
          halign: "right",
        },
        // 3: {
        //   cellWidth: this.pdf.calculateTableWidth(20),
        //   cellPadding: {
        //     top: this.fontSize * 0.1,
        //     bottom: this.fontSize * 0.1,
        //     left: this.fontSize * 0.1,
        //     right: this.fontSize * 0.1,
        //   },
        //   halign: "right",
        // }, // Total Price
      },
      didDrawCell: (data: any) => {
        if (data.section === "head") {
          const { cell, doc } = data;
          const { x, y, width, height } = cell;
          // Draw a rectangle border around each header cell
          doc.setLineWidth(0.1);
          doc.rect(x, y, width, height);
        } else {
          data.doc.setLineWidth(0.1);
        }
      },
      didParseCell: (data: any) => {
        // Make first header cell left-aligned
        if (data.section === "head" && data.column.index === 0) {
          data.cell.styles.halign = "left";
        }
      },
    });

    this.heightUsed = (this.doc as any).lastAutoTable.finalY;
  }

  private addCustomerName() {
    if (!this.data.customerName) {
      return;
    }

    this.doc.setFontSize(this.fontSize);

    const heightPoint = this.TEXT_HEIGHT_MULTIPLIER;

    const supplierText = `Customer: ${this.data.customerName}`;

    this.doc.text(
      supplierText,
      this.margin,
      this.calculateRequiredHeight(heightPoint),
      {
        align: "left",
      },
    );

    this.addHeightUsed(heightPoint);
  }

  public download() {
    let fileName = `wholesale-request`;

    if (this.data.customerName) {
      fileName += `-${this.data.customerName.replace(/\s+/g, "_").toLowerCase()}`;
    }

    fileName += `.pdf`;

    this.pdf.download(fileName);
  }

  public static mapDataToPdf(_payload: any) {}
}
